import React, { useState, useEffect } from 'react';
import { IoArrowForward } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { SERVER } from '../config/config';
import ServerImage from './ServerImage';

const FeaturedProjects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryWidths, setCategoryWidths] = useState({});

  useEffect(() => {
    fetchFeaturedProjects();
  }, []);

  useEffect(() => {
    // Calculate category widths after projects are loaded
    if (projects.length > 0) {
      const widths = {};
      projects.forEach(project => {
        if (project.category) {
          // Create a temporary element to measure text width
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          context.font = '15px system-ui'; // Match the font used in the component
          const metrics = context.measureText(project.category);
          // Add padding and convert to percentage (assuming container width)
          widths[project.id] = Math.min(Math.ceil((metrics.width + 20) / 4), 50); // Cap at 50%
        }
      });
      setCategoryWidths(widths);
    }
  }, [projects]);

  const fetchFeaturedProjects = async () => {
    try {
      const response = await axios.get(`${SERVER}/portfolio?featured=true`);
      // Sort by order_index and take first 6
      const sortedProjects = response.data.items
        .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
        .slice(0, 6);
      setProjects(sortedProjects);
    } catch (error) {
      console.error('Error fetching featured projects:', error);
    } finally {
      setLoading(false);
    }
  };

  // Determine project size based on order_index
  const getProjectSize = (orderIndex) => {
    return orderIndex <= 2 ? 'large' : 'small';
  };

  const largeProjects = projects.filter(p => getProjectSize(p.order_index) === 'large');
  const smallProjects = projects.filter(p => getProjectSize(p.order_index) === 'small');

  if (loading) {
    return (
      <section className="py-0 md:py-16 px-2 md:px-4 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-0 md:py-16 px-2 md:px-4 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between text-center md:text-left items-center md:items-start mb-4 md:mb-12">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold  mb-4">Featured projects</h2>
          <p className="text-gray-400 text-sm md:text-base ">
            A curated selection of our most impactful projects, showcasing creativity, strategy, and design excellence.
          </p>
        </div>
        <button 
          onClick={() => navigate('/portfolio')}
          className="hover:opacity-90 max-md:hidden font-semibold text-white px-6 py-2 rounded-full flex items-center gap-2 transition-colors" 
          style={{ backgroundColor: 'var(--primary-blue)' }}
        >
          Discover all
          <IoArrowForward className="w-8 h-8 bg-white rounded-full text-black p-1" />
        </button>
      </div>

      {/* Large Projects */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-4  md:mb-8">
        {largeProjects.slice(0, 1).map((project) => {
          const clipPathValue = categoryWidths[project.id] || 41;
          return (
            <div key={project.id} className="bg-white border px-2 py-4 border-gray-300 overflow-hidden hover:border-gray-400 transition-colors lg:hidden">
              <div className="flex flex-col">
                {/* Image Section */}
                <div className="relative flex-1">
                  <span className="absolute -top-2 -left-1 text-xs font-medium text-gray-700 z-10">
                    {project.category}
                  </span>
                  <ServerImage 
                    src={project.home_image || project.cover_image} 
                    alt={project.title}
                    fallback="/api/pr1.png"
                    className="w-full h-80 object-cover"
                    style={{
                      clipPath: `polygon(${clipPathValue}% 0, 100% 0, 100% 35%, 100% 70%, 100% 100%, 50% 100%, 0 100%, 0% 70%, 0 4%, ${clipPathValue}% 4%)`
                    }}
                  />
                </div>
                {/* Content Section */}
                <div className="p-2 md:p-6 flex flex-col justify-center flex-1">
                  <div className="flex flex-wrap gap-1 mb-6">
                    {project.tags?.map((tag, index) => (
                      <span key={index} className="flex items-center gap-1 text-xs bg-gray-50 px-2 py-1 rounded-full">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--primary-blue)' }}></span>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="leading-relaxed text-sm font-light md:text-base">{project.description}</p>
                </div>
              </div>
            </div>
          );
        })}
        {largeProjects.map((project) => {
          const clipPathValue = categoryWidths[project.id] || 41;
          return (
            <div key={project.id} className="bg-white border px-2 py-4 border-gray-300 overflow-hidden hover:border-gray-400 transition-colors hidden lg:block">
              <div className="flex">
                {/* Image Section */}
                <div className="relative flex-1">
                  <span className="absolute -top-2 -left-1 text-xs font-medium text-gray-700 z-10">
                    {project.category}
                  </span>
                  <ServerImage 
                    src={project.home_image || project.cover_image} 
                    alt={project.title}
                    fallback="/api/pr1.png"
                    className="w-full h-80 object-cover"
                    style={{
                      clipPath: `polygon(${clipPathValue}% 0, 100% 0, 100% 35%, 100% 70%, 100% 100%, 50% 100%, 0 100%, 0% 70%, 0 4%, ${clipPathValue}% 4%)`
                    }}
                  />
                </div>
                {/* Content Section */}
                <div className="p-6 flex flex-col  justify-start md:pt-10 flex-1">
                  <div className="flex flex-wrap gap-1 mb-6">
                    {project.tags?.map((tag, index) => (
                      <span key={index} className="flex items-center gap-1 text-xs bg-gray-50 px-2 py-1 rounded-full">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--primary-blue)' }}></span>
                        {tag}
                      </span>
                    ))}
                  </div>
                  <p className="leading-relaxed text-base">{project.description}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Small Projects Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-6 mb-4 md:mb-16">
        {smallProjects.map((project) => (
          <div key={project.id} className="bg-white border border-gray-300 p-1 md:p-2 overflow-hidden hover:border-gray-400 transition-colors">
            <div className="relative">
              <span className="absolute -top-1 left-0 text-[10px]  md:text-xs font-medium text-gray-700 z-10">
                {project.category}
              </span>
              <ServerImage 
                src={project.home_image || project.cover_image} 
                alt={project.title}
                fallback="/api/pr1.png"
                className="w-full h-32 md:h-60 object-cover small-project-image"
              />
            </div>
            <div className="p-1 md:p-2">
              <div className="flex flex-wrap gap-0.5 md:gap-3 mb-4">
                {project.tags?.map((tag, index) => (
                  <span key={index} className="flex items-center gap-0.5 md:gap-2 text-[9px] md:text-xs px-2 py-1 bg-gray-50 rounded-full">
                    <span className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full" style={{ backgroundColor: 'var(--primary-blue)' }}></span>
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-sm font-light md:text-base leading-relaxed">{project.description}</p>
            </div>
          </div>
        ))}
       
        
      </div>

      <style jsx>{`
        .small-project-image {
          clip-path: polygon(30% 0, 100% 0, 100% 35%, 100% 70%, 100% 100%, 50% 100%, 0 100%, 0% 70%, 0 7%, 30% 7%);
        }
        
        @media (min-width: 768px) {
          .small-project-image {
            clip-path: polygon(20% 0, 100% 0, 100% 35%, 100% 70%, 100% 100%, 50% 100%, 0 100%, 0% 70%, 0 6%, 20% 6%);
          }
        }
      `}</style>
       <div className='w-full mb-4 flex justify-center'>
          <button 
            onClick={() => navigate('/portfolio')}
            className="hover:opacity-90 md:hidden text-sm font-semibold text-white px-2 py-2 rounded-full flex items-center gap-2 transition-colors" 
            style={{ backgroundColor: 'var(--primary-blue)' }}
          >
            Discover all
            <IoArrowForward className="w-6 h-6 bg-white rounded-full text-black p-1" />
          </button>
        </div>

        
      {/* We Build Section */}
      <div className="text-left mb-4 md:mb-0">
        <h3 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight">
          We Build <span className="text-white px-3 py-1" style={{ backgroundColor: 'var(--primary-blue)' }}>brands</span> and{' '}
          <span className="text-white px-3 py-1" style={{ backgroundColor: 'var(--primary-blue)' }}>websites</span>
        </h3>
        <p className="text-3xl md:text-5xl font-bold text-gray-900 mt-2">That Stand Out.</p>
      </div>

    </section>
  );
};

export default FeaturedProjects;